import * as React from 'react';
import { CPG_SEARCH_URL } from './cpgSites';
import { SearchIcon } from './icons';

// Port of Core/NextJS/packages/cpg-chrome/client/CpgSearch.tsx; React 17 has no useId, so the panel id is fixed.

const PANEL_ID = 'cpg-header-search';
const TOGGLE_SELECTOR = '.cpg-header__search-toggle';
const VIEWPORT_GUTTER = 16;

export interface CpgSearchState {
  open: boolean;
  toggle: (e: React.MouseEvent<HTMLButtonElement>) => void;
  close: (returnFocus?: boolean) => void;
}

export function useCpgSearch(
  containerRef: React.RefObject<HTMLElement>,
  onOpen?: () => void,
): CpgSearchState {
  const [open, setOpen] = React.useState(false);
  const openerRef = React.useRef<HTMLElement | null>(null);

  const close = React.useCallback((returnFocus = false) => {
    setOpen(false);
    if (returnFocus) openerRef.current?.focus();
  }, []);

  const toggle = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (open) {
        close(true);
        return;
      }
      openerRef.current = e.currentTarget;
      onOpen?.();
      setOpen(true);
    },
    [open, close, onOpen],
  );

  React.useEffect(() => {
    if (!open) return undefined;
    const container = containerRef.current;
    const panel = document.getElementById(PANEL_ID);

    const place = () => {
      if (!container || !panel) return;
      const toggleEl = Array.from(
        container.querySelectorAll<HTMLElement>(TOGGLE_SELECTOR),
      ).find((el) => el.getClientRects().length > 0);
      if (!toggleEl) return;
      const containerRect = container.getBoundingClientRect();
      const right = containerRect.right - toggleEl.getBoundingClientRect().right;
      const maxRight = containerRect.width - panel.offsetWidth - VIEWPORT_GUTTER;
      panel.style.setProperty(
        '--cpg-header-search-right',
        `${Math.max(0, Math.min(right, maxRight))}px`,
      );
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(true);
    };
    // Clicking or tabbing outside the panel closes it.
    const onOutside = (e: Event) => {
      if (!(e.target instanceof Element)) return;
      if (panel?.contains(e.target) || e.target.closest(TOGGLE_SELECTOR)) return;
      close();
    };

    place();
    panel?.querySelector('input')?.focus();
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('focusin', onOutside);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('focusin', onOutside);
      window.removeEventListener('resize', place);
    };
  }, [open, close, containerRef]);

  return { open, toggle, close };
}

export function CpgSearchToggle({
  search,
  className,
  tooltip,
}: {
  search: CpgSearchState;
  className: string;
  tooltip?: string;
}) {
  return (
    <button
      type="button"
      className={`${className} cpg-header__search-toggle`}
      data-tooltip={tooltip}
      aria-label="Search the CPG Media network"
      aria-expanded={search.open}
      aria-controls={PANEL_ID}
      onClick={search.toggle}
    >
      <SearchIcon />
    </button>
  );
}

export function CpgSearchPanel({ search }: { search: CpgSearchState }) {
  const action = `${CPG_SEARCH_URL}/search`;

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const query = String(new FormData(e.currentTarget).get('q') ?? '').trim();
    window.open(`${action}?q=${encodeURIComponent(query)}`, '_blank', 'noopener');
  };

  return (
    <div
      id={PANEL_ID}
      className="cpg-header__search"
      hidden={!search.open}
    >
      <p className="cpg-header__search-intro">
        Search across the BevNET CPG Media network including BevNET, Nosh,
        Brewbound, Taste Radio, Nombase and CPG Jobs.
      </p>
      <form
        className="cpg-header__search-form"
        action={action}
        method="get"
        target="_blank"
        role="search"
        onSubmit={onSubmit}
      >
        <div className="cpg-header__search-row">
          <label className="cpg-header__search-field">
            <span className="cpg-header__search-label">
              Search articles, products, etc.
            </span>
            <SearchIcon size={18} />
            <input
              className="cpg-header__search-input"
              type="search"
              name="q"
              maxLength={400}
              placeholder="Search articles, products, etc."
              autoComplete="off"
            />
          </label>
          <button className="cpg-header__search-submit" type="submit">
            Search
          </button>
        </div>
        <p className="cpg-header__search-hint">Results open on cpgmedia.com</p>
      </form>
    </div>
  );
}
